import {Injectable} from '@angular/core';
import {StatusBar, Style} from '@capacitor/status-bar';
import {StorageService} from '../storage/storage.service';
import {PlatformService} from './platform.service';

export type ThemeMode = 'dark' | 'light' | 'system';

const THEME_MODE = 'theme-mode';

/**
 * Dark mode (the design of the app) or light mode (theme/light.scss), chosen in the profile, until then following the
 * system (default). Sets the class of the body and the color of the status bar of the app.
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private mode: ThemeMode = 'system';
  private readonly systemDark = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;

  constructor(
    private storageService: StorageService,
    private platformService: PlatformService
  ) {
    // the system switches between day and night: the theme follows, when chosen
    this.systemDark?.addEventListener?.('change', () => {
      if (this.mode === 'system') {
        this.apply();
      }
    });
  }

  async init() {
    const stored = await this.storageService.get(THEME_MODE);
    this.mode = ['dark', 'light', 'system'].includes(stored) ? stored : 'system';
    await this.apply();
  }

  getMode(): ThemeMode {
    return this.mode;
  }

  async setMode(mode: ThemeMode) {
    this.mode = mode;
    await this.storageService.save(THEME_MODE, mode);
    await this.apply();
  }

  isLight(): boolean {
    return this.mode === 'light' || (this.mode === 'system' && !!this.systemDark && !this.systemDark.matches);
  }

  private async apply() {
    const light = this.isLight();
    document.body.classList.toggle('light', light);
    document.body.classList.toggle('dark', !light);
    if (this.platformService.isApp()) {
      try {
        await StatusBar.setBackgroundColor({color: light ? '#ffffff' : '#232323'});
        // Style.Light: dark text for a light bar, Style.Dark: light text for a dark bar
        await StatusBar.setStyle({style: light ? Style.Light : Style.Dark});
        await StatusBar.show();
      } catch (e) {
      }
    }
  }
}
